import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { t } from '../i18n';
import { api, qs } from '../lib/api';
import { fmtDate, fmtMoney } from '../lib/format';
import type { DocumentType } from '../lib/types';
import { useDebounced } from './ui';

type Result = {
  customers: Array<{ id: number; businessName: string; companyNumber: string | null; status: string; contactName: string | null; phone: string | null; hit: string }>;
  documents: Array<{ id: number; type: DocumentType; typeLabel: string; number: string | null; issueDate: string | null; total: number | null; customerId: number | null; customerName: string | null }>;
};

const HIT_LABEL: Record<string, string> = {
  company_number: 'ח.פ',
  phone: 'טלפון',
  email: 'אימייל',
  contact: 'איש קשר',
  alias: 'שם חלופי',
  address: 'כתובת',
  similar: 'שם דומה',
};

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const dq = useDebounced(q.trim(), 150);
  const navigate = useNavigate();
  const { data, isFetching } = useQuery({
    queryKey: ['search', dq],
    queryFn: ({ signal }) => api<Result>(`/api/search${qs({ q: dq })}`, { signal }),
    enabled: open && dq.length >= 2,
    staleTime: 10_000,
  });

  const items = useMemo(() => {
    const list: Array<{ key: string; go: () => void; group: 'c' | 'd'; node: React.ReactNode }> = [];
    for (const c of data?.customers ?? []) {
      list.push({
        key: `c${c.id}`,
        group: 'c',
        go: () => navigate(`/customers/${c.id}`),
        node: (
          <>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="ellipsis strong">{c.businessName}</div>
              <div className="small muted ellipsis">
                {[c.companyNumber && `ח.פ ${c.companyNumber}`, c.contactName, c.phone].filter(Boolean).join(' · ')}
              </div>
            </div>
            {c.hit !== 'name' ? <span className="badge outline">{HIT_LABEL[c.hit] ?? ''}</span> : null}
          </>
        ),
      });
    }
    for (const d of data?.documents ?? []) {
      list.push({
        key: `d${d.id}`,
        group: 'd',
        go: () => navigate(`${d.customerId ? `/customers/${d.customerId}` : '/documents'}?doc=${d.id}`),
        node: (
          <>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="ellipsis">
                <span className="strong">{d.typeLabel} <bdi className="tabular">{d.number}</bdi></span>
                {d.customerName ? <span className="muted"> · {d.customerName}</span> : null}
              </div>
              <div className="small muted">{fmtDate(d.issueDate)}</div>
            </div>
            <span className="tabular small">{fmtMoney(d.total)}</span>
          </>
        ),
      });
    }
    return list;
  }, [data, navigate]);

  useEffect(() => setActive(0), [dq]);
  useEffect(() => {
    if (!open) setQ('');
  }, [open]);

  if (!open) return null;
  const choose = (i: number) => {
    const it = items[i];
    if (!it) return;
    it.go();
    onClose();
  };
  return createPortal(
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="palette" role="dialog" aria-modal="true" aria-label={t.common.search}>
        <input
          className="palette-input"
          autoFocus
          placeholder={t.nav.searchPlaceholder}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => Math.min(items.length - 1, a + 1));
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(0, a - 1));
            }
            if (e.key === 'Enter') choose(active);
          }}
          role="combobox"
          aria-expanded={items.length > 0}
          aria-controls="palette-results"
        />
        <div className="palette-results" id="palette-results" role="listbox">
          {dq.length < 2 ? (
            <div className="menu-group-label" style={{ padding: 14 }}>הקלידו לפחות 2 תווים — שם לקוח, ח.פ, טלפון, איש קשר, עיר או מספר מסמך</div>
          ) : items.length === 0 ? (
            <div className="menu-group-label" style={{ padding: 14 }}>{isFetching ? t.common.loading : 'לא נמצאו תוצאות'}</div>
          ) : (
            items.map((it, i) => (
              <div key={it.key}>
                {i === 0 || items[i - 1]!.group !== it.group ? (
                  <div className="menu-group-label">{it.group === 'c' ? t.nav.customers : t.nav.documents}</div>
                ) : null}
                <button role="option" aria-selected={i === active} className={`menu-item ${i === active ? 'active' : ''}`} onMouseEnter={() => setActive(i)} onClick={() => choose(i)}>
                  {it.node}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
